import { IDynamicModelOptions, IDynamicUpdateOnValueChange } from '../models';

/**
 * The fields these helpers touch, shared by IDynamicControl (form containers
 * from the API) and IDynamicModel (rendered controls).
 */
export interface FormFillControl {
  dynamicControlType?: string;
  options?: IDynamicModelOptions;
  valueProperty?: string;
}

/**
 * WI 34031: options a form-fill job typeahead carries. Kept beside the helpers
 * rather than on IDynamicModelOptions because only the form-container render
 * path sets it.
 */
export interface IFormFillJobScopeOptions extends IDynamicModelOptions {
  /**
   * Sent to Sparta `/api/jobs/dropdown` as `anyActiveJob=true`: any active job
   * on any company, not just the user's assigned companies/jobs. The server
   * forces active-only when it is set.
   */
  anyActiveJob?: boolean;
}

/**
 * WI 34031: a job typeahead rendered inside a form container (HSE form-fill)
 * opts into the any-active-job scope. Pages that place
 * `ups-dynamic-typeahead-job` directly (dashboards, timekeeping, sign-in
 * sheets) build their own config and never pass through here, so they keep the
 * per-user scope.
 */
export function applyFormFillJobScope(control: FormFillControl): void {
  if (control.dynamicControlType === 'typeahead-job') {
    const options: IFormFillJobScopeOptions = {
      ...control.options,
      anyActiveJob: true,
    };
    control.options = options;
  }
}

interface ILegacyDynamicModelOptions extends IDynamicModelOptions {
  updateOnSelection?: Array<IDynamicUpdateOnValueChange>;
}

/**
 * Bug 36548: removes options that exist only for form-fill rendering, so the
 * form builder never saves them into a stored definition (the job-scope bypass
 * would then outlive the code that sets it).
 */
export function stripFormFillOnlyOptions(
  options: IFormFillJobScopeOptions | undefined
): void {
  delete options?.anyActiveJob;
}

/**
 * Prepares each control of a rendered form container, top-level and nested
 * (moved out of DynamicRenderService's control loop): migrates legacy option
 * names, then applies the WI 34031 form-fill job scope. The form builder gets
 * no job scope, and any stamp already stored is removed (Bug 36548).
 */
export function prepareFormContainerControl(
  control: FormFillControl,
  isFormBuilder = false
): void {
  const legacyOptions = control?.options as
    ILegacyDynamicModelOptions | undefined;
  if (control?.options && legacyOptions?.updateOnSelection) {
    // January 11, 2022: updateOnSelection was renamed to updateOnValueChange.
    control.options.updateOnValueChange = legacyOptions.updateOnSelection;
    delete legacyOptions.updateOnSelection;
  }
  if (isFormBuilder) {
    stripFormFillOnlyOptions(control?.options);
  } else {
    applyFormFillJobScope(control);
  }
}

/**
 * Merges the `anyActiveJob` flag into a job typeahead's custom query params
 * when its options opt in, preserving any params already set (e.g. the
 * closed-jobs `status`). Returns the params unchanged otherwise.
 */
export function withFormFillJobScope(
  customQueryParams: unknown,
  options: IFormFillJobScopeOptions | undefined
): unknown {
  if (options?.anyActiveJob !== true) {
    return customQueryParams;
  }
  const params =
    customQueryParams && typeof customQueryParams === 'object'
      ? customQueryParams
      : {};
  return { ...params, anyActiveJob: true };
}
